import { Injectable } from '@angular/core';
import {
  QuestionGroupSortAttribute,
  SortDirection
} from '@generated/base-types';
import { FileSaverService } from '@new/common/modal/export-question-list-modal/export-service/file-saver.service';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { parseError } from '@new/common/utils/error-parser';
import {
  assertIsDefined,
  isDefined
} from '@new/common/utils/type-guards/is-defined';
import { TranslateService } from '@ngx-translate/core';
import { Action, State, Store } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { catchError, Observable, filter, switchMap, tap } from 'rxjs';
import { ToastsService } from '../../../../common/services/toasts.service';
import { AssignLabelsGQL } from '../../lib/services/labels/assign-labels.generated';
import { DuplicateQuestionGQL } from '../../lib/services/question-groups/duplicate-question.generated';
import {
  LoadPoolQuestionGroupsGQL,
  LoadPoolQuestionGroupsQuery
} from '../../lib/services/question-groups/load-question-groups.generated';
import { AssignExamsGQL } from '../../lib/services/tasks/assign-exam.generated';
import { DeleteQuestionGroupsGQL } from '../../lib/services/tasks/delete-question-groups.generated';
import { RevisionDocumentGQL } from '../../lib/services/tasks/revision-document.generated';
import { SetAffiliationGQL } from '../../lib/services/tasks/set-affiliation.generated';
import { SetBlueprintGQL } from '../../lib/services/tasks/set-blueprint.generated';
import { SetRevisionStatusGQL } from '../../lib/services/tasks/set-revision-status.generated';
import { SetRevisionYearGQL } from '../../lib/services/tasks/set-revision-year.generated';
import { SetSourceLanguageGQL } from '../../lib/services/tasks/set-source-language.generated';
import { SetSupervisorGQL } from '../../lib/services/tasks/set-supervisor.generated';
import { updateLabels } from '../../lib/utils/update-labels';
import {
  selectPoolAffiliations,
  selectPoolId,
  selectPoolLabels,
  selectPoolRevisionStatuses,
  selectPoolUnlockedExams
} from '../pool/pool.state.model';
import { LoadQuestionGroup } from '../question-group-edit/question-group-edit.actions';
import * as Act from './question-groups.actions';
import {
  questionGroupsStateDefaults,
  QuestionGroupsStateModel
} from './question-groups.state.model';

@State<QuestionGroupsStateModel>({
  name: 'questionGroups',
  defaults: questionGroupsStateDefaults,
  children: []
})
@Injectable()
export class QuestionGroupsState {
  constructor(
    private readonly store: Store,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService,
    private readonly fileSaver: FileSaverService,
    private readonly loadQuestionGroupsService: LoadPoolQuestionGroupsGQL,
    private readonly deleteQuestionGroupsService: DeleteQuestionGroupsGQL,
    private readonly setSupervisorService: SetSupervisorGQL,
    private readonly setRevisionYearService: SetRevisionYearGQL,
    private readonly setAffiliationService: SetAffiliationGQL,
    private readonly setLanguageService: SetSourceLanguageGQL,
    private readonly setBlueprintService: SetBlueprintGQL,
    private readonly setRevisionStatusService: SetRevisionStatusGQL,
    private readonly revisionDocumentGQL: RevisionDocumentGQL,
    private readonly assignExamService: AssignExamsGQL,
    private readonly assignLabelsService: AssignLabelsGQL,
    private readonly duplicateQuestionGroupGQL: DuplicateQuestionGQL
  ) {}

  /* Query actions */

  @Action(Act.LoadQuestionGroups)
  public loadQuestionGroups(
    ctx: StateContext<QuestionGroupsStateModel>
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId => this.loadQuestionGroupsService.fetch({ poolId })),
      takeGraphQLResult(),
      switchMap((res: LoadPoolQuestionGroupsQuery) =>
        ctx.dispatch(
          new Act.LoadQuestionGroupsSuccess(res.pool.questionGroupList.items)
        )
      ),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadQuestionGroupsFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadQuestionGroupsSuccess)
  public loadQuestionGroupsSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { questionGroups }: Act.LoadQuestionGroupsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: questionGroups,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadQuestionGroupsFailure)
  public loadQuestionGroupsFailure(
    ctx: StateContext<QuestionGroupsStateModel>,
    { error }: Act.LoadQuestionGroupsFailure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'failure', error } }
    });
  }

  @Action(Act.SetSelectedQuestionGroupIds)
  public setSelectedQuestionGroupIds(
    ctx: StateContext<QuestionGroupsStateModel>,
    { ids }: Act.SetSelectedQuestionGroupIds
  ): void {
    ctx.patchState({ selectedIds: ids });
  }

  @Action(Act.ResetSelectedIds)
  public resetSelectedIds(ctx: StateContext<QuestionGroupsStateModel>): void {
    ctx.patchState({ selectedIds: [] });
  }

  @Action(Act.SetSortAttributes)
  public setSortAttributes(
    ctx: StateContext<QuestionGroupsStateModel>,
    { sortAttributes }: Act.SetSortAttributes
  ): void {
    ctx.patchState({ sortAttributes });
  }

  @Action(Act.ResetTableState)
  public resetTableState(ctx: StateContext<QuestionGroupsStateModel>): void {
    ctx.patchState({
      items: { ...ctx.getState().items, data: [] },
      selectedIds: []
    });
  }

  @Action(Act.ResetSortAttributes)
  public resetSortAttributes(
    ctx: StateContext<QuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      sortAttributes: [
        {
          attribute: QuestionGroupSortAttribute.SequentialNumber,
          direction: SortDirection.Asc
        }
      ]
    });
  }

  /* Mutation actions */

  @Action(Act.Failure)
  public failure(
    ctx: StateContext<QuestionGroupsStateModel>,
    { error }: Act.Failure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'failure', error } }
    });
    this.toasts.addWarning(this.translate.instant('error_modal.title'));
  }

  @Action(Act.DeleteQuestionGroups)
  public deleteQuestionGroups(
    ctx: StateContext<QuestionGroupsStateModel>,
    { questionGroupIds }: Act.DeleteQuestionGroups
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to delete question groups');

    return this.deleteQuestionGroupsService
      .mutate({ poolId, questionGroupIds })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(new Act.DeleteQuestionGroupsSuccess(questionGroupIds))
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.DeleteQuestionGroupsSuccess)
  public deleteQuestionGroupsSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { questionGroupIds }: Act.DeleteQuestionGroupsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.filter(qg => !questionGroupIds.includes(qg.id)),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.destroy', {
        resource: this.translate.instant('common.models.question')
      })
    );
  }

  @Action(Act.SetSupervisor)
  public setSupervisor(
    ctx: StateContext<QuestionGroupsStateModel>,
    { supervisor, questionGroupIds }: Act.SetSupervisor
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set supervisor');

    return this.setSupervisorService
      .mutate({ poolId, questionGroupIds, user: supervisor })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetSupervisorSuccess(supervisor, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetSupervisorSuccess)
  public setSupervisorSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { supervisor, questionGroupIds }: Act.SetSupervisorSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.map(qg =>
            questionGroupIds.includes(qg.id) ? { ...qg, supervisor } : qg
          ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.supervisor')
      })
    );
  }

  @Action(Act.SetRevisionYear)
  public setRevisionYear(
    ctx: StateContext<QuestionGroupsStateModel>,
    { revisionYear, questionGroupIds }: Act.SetRevisionYear
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set revision year');

    return this.setRevisionYearService
      .mutate({ poolId, questionGroupIds, revisionYear })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetRevisionYearSuccess(revisionYear, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetRevisionYearSuccess)
  public setRevisionYearSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { revisionYear, questionGroupIds }: Act.SetRevisionYearSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg =>
          questionGroupIds.includes(qg.id)
            ? {
                ...qg,
                revisionYear
              }
            : qg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.revision_year')
      })
    );
  }

  @Action(Act.SetAffiliation)
  public setAffiliation(
    ctx: StateContext<QuestionGroupsStateModel>,
    { affiliationId, questionGroupIds }: Act.SetAffiliation
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set affiliation');

    return this.setAffiliationService
      .mutate({ poolId, questionGroupIds, affiliationId })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetAffiliationSuccess(affiliationId, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetAffiliationSuccess)
  public setAffiliationSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { affiliationId, questionGroupIds }: Act.SetAffiliationSuccess
  ): void {
    const affiliation = this.store
      .selectSnapshot(selectPoolAffiliations)
      .find(({ id }) => id === affiliationId);
    assertIsDefined(affiliation);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg =>
          questionGroupIds.includes(qg.id)
            ? {
                ...qg,
                affiliation
              }
            : qg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.affiliation')
      })
    );
  }

  @Action(Act.SetSourceLanguage)
  public setSourceLanguage(
    ctx: StateContext<QuestionGroupsStateModel>,
    { language, questionGroupIds }: Act.SetSourceLanguage
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set source language');

    return this.setLanguageService
      .mutate({ poolId, questionGroupIds, language })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.SetSourceLanguageSuccess(language, questionGroupIds)
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetSourceLanguageSuccess)
  public setSourceLanguageSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { language, questionGroupIds }: Act.SetSourceLanguageSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg =>
          questionGroupIds.includes(qg.id)
            ? {
                ...qg,
                sourceLanguage: language
              }
            : qg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.sourceLanguage')
      })
    );
  }

  @Action(Act.SetBlueprint)
  public setBlueprint(
    ctx: StateContext<QuestionGroupsStateModel>,
    { categories, questionGroupIds }: Act.SetBlueprint
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set blueprint');

    return this.setBlueprintService
      .mutate({
        poolId,
        questionGroupIds,
        category0Id: categories[0],
        category1Id: categories[1],
        category2Id: categories[2],
        category3Id: categories[3],
        category4Id: categories[4],
        category5Id: categories[5],
        category6Id: categories[6],
        category7Id: categories[7]
      })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => ctx.dispatch(new Act.SetBlueprintSuccess())),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetBlueprintSuccess)
  public setBlueprintSuccess(
    ctx: StateContext<QuestionGroupsStateModel>
  ): void {
    ctx.dispatch(new Act.LoadQuestionGroups());

    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.dimension')
      })
    );
  }

  @Action(Act.SetRevisionStatus)
  public setRevisionStatus(
    ctx: StateContext<QuestionGroupsStateModel>,
    {
      revisionStatusId,
      questionGroupIds,
      fromFormEditor
    }: Act.SetRevisionStatus
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to set revision status');

    return this.setRevisionStatusService
      .mutate({ poolId, questionGroupIds, revisionStatusId })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => {
          const actions: unknown[] = [
            new Act.SetRevisionStatusSuccess(revisionStatusId, questionGroupIds)
          ];

          // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
          if (fromFormEditor === true) {
            actions.push(new LoadQuestionGroup(questionGroupIds[0], poolId));
          }

          return ctx.dispatch(actions);
        }),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.SetRevisionStatusSuccess)
  public setRevisionStatusSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { revisionStatusId, questionGroupIds }: Act.SetRevisionStatusSuccess
  ): void {
    const revisionStatus = this.store
      .selectSnapshot(selectPoolRevisionStatuses)
      .find(({ id }) => id === revisionStatusId);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg =>
          questionGroupIds.includes(qg.id)
            ? {
                ...qg,
                revisionStatus: revisionStatus
                  ? {
                      id: revisionStatus.id,
                      shortName: revisionStatus.shortName,
                      description: revisionStatus.description
                    }
                  : // eslint-disable-next-line unicorn/no-null
                    null
              }
            : qg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.revision_status')
      })
    );
  }

  @Action(Act.CreateRevisionDocument)
  public createRevisionDocument(
    ctx: StateContext<QuestionGroupsStateModel>,
    { displayOptions }: Act.CreateRevisionDocument
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to create revision document');

    return this.revisionDocumentGQL.fetch({ poolId, displayOptions }).pipe(
      takeGraphQLResult(),
      tap(res => {
        const file = res.pool.revisionDocument?.url;
        if (file) {
          this.fileSaver.saveAs(file);
        }
      }),
      switchMap(() => ctx.dispatch(new Act.CreateRevisionDocumentSuccess())),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.CreateRevisionDocumentSuccess)
  public createRevisionDocumentSuccess(
    ctx: StateContext<QuestionGroupsStateModel>
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.create', {
        resource: this.translate.instant('common.models.revision_document')
      })
    );
  }

  @Action(Act.AssignExam)
  public assignExam(
    ctx: StateContext<QuestionGroupsStateModel>,
    {
      questionGroupIds,
      examIdsToAdd,
      examIdsToRemove,
      fromFormEditor
    }: Act.AssignExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to assign exams');

    return this.assignExamService
      .mutate({ poolId, questionGroupIds, examIdsToAdd, examIdsToRemove })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => {
          const actions: unknown[] = [
            new Act.AssignExamSuccess(
              questionGroupIds,
              examIdsToAdd,
              examIdsToRemove
            )
          ];

          // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
          if (fromFormEditor === true) {
            actions.push(new LoadQuestionGroup(questionGroupIds[0], poolId));
          }

          return ctx.dispatch(actions);
        }),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.AssignExamSuccess)
  public assignExamSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { questionGroupIds, examIdsToAdd, examIdsToRemove }: Act.AssignExamSuccess
  ): void {
    const unlockedExams = this.store.selectSnapshot(selectPoolUnlockedExams);

    const updatedTQGs = ctx.getState().items.data?.map(qg => {
      if (!questionGroupIds.includes(qg.id)) return qg;

      const remainingExamsInGroup = qg.futureExams.filter(
        exam => !examIdsToRemove.includes(exam.id)
      );

      const examsToAddToGroup = examIdsToAdd
        .map(id => unlockedExams.find(exam => exam.id === id))
        .filter(isDefined)
        .filter(
          exam =>
            !remainingExamsInGroup.some(existing => existing.id === exam.id)
        );

      return {
        ...qg,
        futureExams: [...remainingExamsInGroup, ...examsToAddToGroup]
      };
    });

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: updatedTQGs,
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.assign_exam')
      })
    );
  }

  @Action(Act.AssignQGLabels)
  public assignQGLabels(
    ctx: StateContext<QuestionGroupsStateModel>,
    {
      labelIdsToAdd,
      labelIdsToRemove,
      questionGroupIds,
      fromFormEditor
    }: Act.AssignQGLabels
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(
      poolId,
      'Pool ID is required to assign question group labels'
    );

    return this.assignLabelsService
      .mutate({ poolId, questionGroupIds, labelIdsToAdd, labelIdsToRemove })
      .pipe(
        takeGraphQLResult(),
        switchMap(() => {
          const actions: unknown[] = [
            new Act.AssignQGLabelsSuccess(
              labelIdsToAdd,
              labelIdsToRemove,
              questionGroupIds
            )
          ];

          // FIXME: 'fromFormEditor' This is a workaround to ensure that the question group state is reloaded after performing actions - the question group editor state is never updated because does not have question-gorups
          if (fromFormEditor === true) {
            actions.push(new LoadQuestionGroup(questionGroupIds[0], poolId));
          }

          return ctx.dispatch(actions);
        }),
        catchError((err: unknown) =>
          ctx.dispatch(new Act.Failure(parseError(err)))
        )
      );
  }

  @Action(Act.AssignQGLabelsSuccess)
  public assignQGLabelsSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    {
      labelIdsToAdd,
      labelIdsToRemove,
      questionGroupIds
    }: Act.AssignQGLabelsSuccess
  ): void {
    const allLabels = this.store.selectSnapshot(selectPoolLabels);

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg =>
          questionGroupIds.includes(qg.id)
            ? {
                ...qg,
                labels: updateLabels(
                  allLabels,
                  qg.labels,
                  labelIdsToAdd,
                  labelIdsToRemove
                )
              }
            : qg
        ),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.update', {
        resource: this.translate.instant('common.models.question')
      })
    );
  }

  @Action(Act.UpdateQGLabel)
  public updateQGLabel(
    ctx: StateContext<QuestionGroupsStateModel>,
    { label: updatedLabel }: Act.UpdateQGLabel
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg => {
          const hasLabel = qg.labels.some(l => l.id === updatedLabel.id);

          return hasLabel
            ? {
                ...qg,
                labels: qg.labels.map(l =>
                  l.id === updatedLabel.id ? updatedLabel : l
                )
              }
            : qg;
        }),
        mutation: { state: 'success' }
      }
    });
  }

  @Action(Act.RemoveQGLabel)
  public removeQGLabel(
    ctx: StateContext<QuestionGroupsStateModel>,
    { id }: Act.RemoveQGLabel
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.map(qg => ({
          ...qg,
          labels: qg.labels.filter(l => l.id !== id)
        })),
        mutation: { state: 'success' }
      }
    });
  }

  @Action(Act.DuplicateQuestionGroup)
  public duplicateQuestionGroup(
    ctx: StateContext<QuestionGroupsStateModel>,
    {
      questionGroupId,
      targetPool,
      sequentialNumber
    }: Act.DuplicateQuestionGroup
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const poolId = this.store.selectSnapshot(selectPoolId);
    assertIsDefined(poolId, 'Pool ID is required to assign exams');

    return this.duplicateQuestionGroupGQL
      .mutate({ poolId, questionGroupId, targetPoolId: targetPool.id })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(
            new Act.DuplicateQuestionGroupSuccess(
              sequentialNumber,
              targetPool.name
            )
          )
        ),
        catchError((err: unknown) =>
          ctx.dispatch(
            new Act.DuplicateQuestionGroupFailure(
              parseError(err),
              targetPool.id,
              questionGroupId
            )
          )
        )
      );
  }

  @Action(Act.DuplicateQuestionGroupSuccess)
  public duplicateQuestionGroupSuccess(
    ctx: StateContext<QuestionGroupsStateModel>,
    { sequentialNumber, targetPoolName }: Act.DuplicateQuestionGroupSuccess
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'success' } }
    });

    this.toasts.addSuccess(
      this.translate.instant(
        'pool.question_list.toolbar.duplicate_question_modal.messages.duplicated',
        {
          sequentialNumber,
          poolName: targetPoolName
        }
      )
    );
  }

  @Action(Act.DuplicateQuestionGroupFailure)
  public duplicateQuestionGroupFailure(
    ctx: StateContext<QuestionGroupsStateModel>,
    { error, targetPoolId, questionGroupId }: Act.DuplicateQuestionGroupFailure
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        mutation: { state: 'failure', error }
      }
    });

    this.toasts.addWarning(
      this.translate.instant(
        'pool.question_list.toolbar.duplicate_question_modal.messages.error',
        { questionGroupId, poolId: targetPoolId }
      )
    );
  }
}
